import React, { useEffect, useMemo, useRef, useState } from 'react';
import url from '../../../../serverUrl';
import { api as axios } from '../../../../api';
import { fmtPrice, fmtQty, fmtUsd } from './format';
import './pro.css';

// ---------------------------------------------------------------------------
// Cumulative depth chart (bids left in teal, asks right in pink) drawn as
// SVG from /hootdex/orderbook. Hover shows price, cumulative size and value.
// ---------------------------------------------------------------------------

const PAD = { l: 8, r: 70, t: 16, b: 26 };

export default function DepthChart({ orderSymbol, refPrice }) {
  const [book, setBook] = useState(null);
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const hasLevels = Boolean(book?.bids?.length || book?.asks?.length);
  // Draw in real pixels (not a stretched viewBox) so text stays crisp.
  const [size, setSize] = useState({ w: 1000, h: 520 });
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      if (width > 0 && height > 0) {
        const w = Math.round(width);
        const h = Math.round(height);
        setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasLevels]);
  const W = size.w;
  const H = size.h;
  const ref = Number(refPrice) > 0 ? Number(Number(refPrice).toPrecision(5)) : undefined;

  useEffect(() => {
    if (!orderSymbol) return undefined;
    let cancelled = false;
    let timer;
    setBook(null);
    const load = () =>
      axios
        .get(`${url}/hootdex/orderbook`, {
          params: { symbol: orderSymbol, depth: 60, group: 1, ref },
          timeout: 10000
        })
        .then((res) => {
          if (!cancelled && res.data?.symbol === orderSymbol) setBook(res.data);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timer = setTimeout(load, 3000);
        });
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderSymbol, ref]);

  const geo = useMemo(() => {
    const bids = (book?.bids || []).filter((l) => l.price > 0);
    const asks = (book?.asks || []).filter((l) => l.price > 0);
    if (!bids.length && !asks.length) return null;
    const prices = [...bids.map((l) => l.price), ...asks.map((l) => l.price)];
    let lo = Math.min(...prices);
    let hi = Math.max(...prices);
    if (hi === lo) {
      lo *= 0.99;
      hi *= 1.01;
    }
    const maxTot = Math.max(bids[bids.length - 1]?.total || 0, asks[asks.length - 1]?.total || 0) * 1.08 || 1;
    const x = (p) => PAD.l + ((p - lo) / (hi - lo)) * (W - PAD.l - PAD.r);
    const y = (v) => H - PAD.b - (v / maxTot) * (H - PAD.t - PAD.b);
    const step = (levels, dir) => {
      // Staircase path: bids walk left from the best bid, asks walk right.
      if (!levels.length) return '';
      let d = `M${x(levels[0].price)},${y(0)}`;
      let prevTot = 0;
      levels.forEach((l) => {
        d += ` L${x(l.price)},${y(prevTot)} L${x(l.price)},${y(l.total)}`;
        prevTot = l.total;
      });
      const edge = dir < 0 ? PAD.l : W - PAD.r;
      d += ` L${edge},${y(prevTot)}`;
      return d;
    };
    const bidLine = step(bids, -1);
    const askLine = step(asks, 1);
    const close = (line, dir) =>
      line ? `${line} L${dir < 0 ? PAD.l : W - PAD.r},${y(0)} Z` : '';
    const ticks = [];
    for (let i = 0; i <= 4; i++) ticks.push(lo + ((hi - lo) * i) / 4);
    const yt = [];
    for (let i = 1; i <= 4; i++) yt.push((maxTot / 1.08) * (i / 4));
    return { bids, asks, lo, hi, x, y, bidLine, askLine, bidArea: close(bidLine, -1), askArea: close(askLine, 1), ticks, yt };
  }, [book, W, H]);

  const onMove = (e) => {
    if (!geo || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const price = geo.lo + ((px - PAD.l) / (W - PAD.l - PAD.r)) * (geo.hi - geo.lo);
    const mid = book?.bestBid && book?.bestAsk ? (book.bestBid + book.bestAsk) / 2 : null;
    const sideBid = mid ? price <= mid : geo.bids.length && price <= geo.bids[0].price;
    let lvl = null;
    if (sideBid) {
      for (const l of geo.bids) if (l.price >= price) lvl = l;
    } else {
      for (const l of geo.asks) if (l.price <= price) lvl = l;
    }
    if (!lvl) lvl = sideBid ? geo.bids[0] : geo.asks[0];
    if (!lvl) return setHover(null);
    const notional = (sideBid ? geo.bids : geo.asks)
      .filter((l) => (sideBid ? l.price >= lvl.price : l.price <= lvl.price))
      .reduce((s, l) => s + l.size * l.price, 0);
    setHover({ side: sideBid ? 'bid' : 'ask', lvl, notional, px: geo.x(lvl.price), py: geo.y(lvl.total) });
  };

  return (
    <div className="x-depth">
      <div className="x-depth-legend">
        <span><i className="x-dot x-dot-up" /> Bids {book?.bestBid ? fmtPrice(book.bestBid) : '—'}</span>
        <span className="x-muted">
          Spread{' '}
          {book?.spread != null
            ? book.decimals != null
              ? book.spread.toFixed(book.decimals)
              : fmtPrice(book.spread)
            : '—'}
          {book?.spreadPct != null ? ` (${book.spreadPct.toFixed(3)}%)` : ''}
        </span>
        <span><i className="x-dot x-dot-down" /> Asks {book?.bestAsk ? fmtPrice(book.bestAsk) : '—'}</span>
      </div>
      {!geo ? (
        <div className="x-empty x-depth-empty">{book ? 'No resting orders for this market.' : 'Loading depth…'}</div>
      ) : (
        <div className="x-depth-wrap" ref={wrapRef}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            role="img"
            aria-label="Order book depth chart"
          >
            {geo.yt.map((v) => (
              <g key={v}>
                <line x1={PAD.l} x2={W - PAD.r} y1={geo.y(v)} y2={geo.y(v)} className="x-grid" />
                <text x={W - PAD.r + 6} y={geo.y(v) + 4} className="x-axis">{fmtQty(v, 2)}</text>
              </g>
            ))}
            {geo.ticks.map((p, i) => (
              <text key={i} x={geo.x(p)} y={H - 6} className="x-axis" textAnchor={i === 0 ? 'start' : i === 4 ? 'end' : 'middle'}>
                {fmtPrice(p)}
              </text>
            ))}
            <path d={geo.bidArea} className="x-depth-bid-area" />
            <path d={geo.askArea} className="x-depth-ask-area" />
            <path d={geo.bidLine} className="x-depth-bid-line" />
            <path d={geo.askLine} className="x-depth-ask-line" />
            {hover && (
              <g>
                <line x1={hover.px} x2={hover.px} y1={PAD.t} y2={H - PAD.b} className="x-cross" />
                <circle cx={hover.px} cy={hover.py} r="5" className={hover.side === 'bid' ? 'x-depth-dot-bid' : 'x-depth-dot-ask'} />
              </g>
            )}
          </svg>
          {hover && (
            <div
              className="x-depth-tip"
              style={{ left: `${(hover.px / W) * 100}%`, transform: hover.px > W / 2 ? 'translateX(-108%)' : 'translateX(8%)' }}
            >
              <div className={hover.side === 'bid' ? 'x-up' : 'x-down'}>{hover.side === 'bid' ? 'Bid' : 'Ask'} {fmtPrice(hover.lvl.price)}</div>
              <div><span className="x-muted">Total size</span> {fmtQty(hover.lvl.total)}</div>
              <div><span className="x-muted">Total value</span> {fmtUsd(hover.notional, { compact: true })}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
